import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { exportStrategy, getExportFormats } from '../api';
import { track, Events } from '../analytics';
import Button from './Button';
import Spinner from './Spinner';

// Export a strategy to external-platform code (backtrader / PineScript). Pass ONE
// source: strategyId, backtestId (preconfigures the run's params), or graph+name
// (the live builder / AI-chat graph). Each generated format counts against the
// user's daily export quota; results are cached per-format for the modal session
// so re-viewing an already-generated tab is free.
export default function ExportStrategyModal({ strategyId, backtestId, graph, name, onClose, onRequiresSignup }) {
  const navigate = useNavigate();
  const [formats, setFormats] = useState([]);
  const [active, setActive] = useState(null);
  const [cache, setCache] = useState({});      // format key -> result payload
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [upgrade, setUpgrade] = useState('');   // set when quota exceeded
  const [copied, setCopied] = useState(false);

  const basePayload = useCallback((fmt) => {
    const p = { format: fmt };
    if (backtestId) p.backtest_id = backtestId;
    else if (strategyId) p.strategy_id = strategyId;
    else if (graph) { p.graph = graph; if (name) p.name = name; }
    return p;
  }, [backtestId, strategyId, graph, name]);

  const generate = useCallback(async (fmt) => {
    setError('');
    setUpgrade('');
    setLoading(true);
    try {
      const { data } = await exportStrategy(basePayload(fmt));
      setCache((prev) => ({ ...prev, [fmt]: data }));
      track(Events.STRATEGY_EXPORTED, { format: fmt, source: backtestId ? 'backtest' : strategyId ? 'strategy' : 'graph' });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401 || err?.response?.data?.requires_signup) {
        onRequiresSignup && onRequiresSignup('Sign up to export strategies to code.');
        onClose && onClose();
        return;
      }
      if (status === 403 || err?.response?.data?.requires_upgrade) {
        setUpgrade(detail || 'You have reached your daily export limit.');
        return;
      }
      setError(detail || 'Could not generate the export. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [basePayload, backtestId, strategyId, onRequiresSignup, onClose]);

  // Load formats once, then generate the first tab.
  useEffect(() => {
    let mounted = true;
    getExportFormats()
      .then((res) => {
        if (!mounted) return;
        const list = res.data?.formats || [];
        setFormats(list);
        if (list.length) setActive(list[0].key);
      })
      .catch(() => { if (mounted) setError('Could not load export options.'); });
    return () => { mounted = false; };
  }, []);

  // Generate the active tab if we haven't already.
  useEffect(() => {
    if (active && !cache[active]) generate(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const result = active ? cache[active] : null;

  const handleCopy = async () => {
    if (!result?.code) return;
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — user can select manually */ }
  };

  const handleDownload = () => {
    if (!result?.code) return;
    const blob = new Blob([result.code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename || 'strategy.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const unsupported = result?.unsupported || [];
  const warnings = result?.warnings || [];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
        padding: 24, maxWidth: 760, width: '100%', position: 'relative',
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: 14, right: 16, background: 'none', border: 'none',
          color: 'var(--muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1,
        }}>✕</button>

        <h3 style={{ margin: '0 0 4px', color: 'var(--text)', fontSize: 18 }}>Export strategy</h3>
        <p style={{ color: 'var(--muted)', margin: '0 0 14px', fontSize: 12.5, lineHeight: 1.5 }}>
          Portable starting point for other tools — broker/fill models differ, so verify before trading.
        </p>

        {/* Format tabs */}
        <div className="tabbar" style={{ alignSelf: 'flex-start', marginBottom: 14, flexWrap: 'wrap' }}>
          {formats.map((f) => (
            <button
              key={f.key}
              onClick={() => setActive(f.key)}
              className="tab"
              style={{
                background: active === f.key ? 'var(--teal)' : 'transparent',
                color: active === f.key ? 'var(--bg)' : 'var(--text)',
                border: 'none', borderRadius: 'var(--radius-sm)', padding: '6px 12px',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 34,
              }}
            >
              {f.name}
            </button>
          ))}
        </div>

        {upgrade ? (
          <div style={{
            background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.4)',
            borderRadius: 8, padding: 16, color: 'var(--text)', fontSize: 13.5, lineHeight: 1.6,
          }}>
            <div style={{ marginBottom: 12 }}>⚠️ {upgrade}</div>
            <Button size="sm" onClick={() => { onClose && onClose(); navigate('/pricing'); }}>See plans</Button>
          </div>
        ) : error ? (
          <div style={{ color: 'var(--red)', fontSize: 13, padding: '12px 0' }}>{error}</div>
        ) : loading || !result ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)', padding: '24px 0' }}>
            <Spinner size={18} /> Generating…
          </div>
        ) : (
          <>
            {/* Coverage banner */}
            {(unsupported.length > 0 || warnings.length > 0) ? (
              <div style={{
                background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.35)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12.5, color: 'var(--text)', lineHeight: 1.6,
              }}>
                <strong>Partial export.</strong>{' '}
                {unsupported.length > 0 && (
                  <>These parts have no equivalent on this platform and were left out:{' '}
                    {unsupported.map((u) => u.type).join(', ')}. </>
                )}
                {warnings.map((w, i) => <div key={i} style={{ color: 'var(--muted)' }}>• {w}</div>)}
              </div>
            ) : (
              <div style={{
                background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 12.5, color: 'var(--green)',
              }}>
                ✓ Fully exported — every node translated.
              </div>
            )}

            <pre style={{
              flex: 1, overflow: 'auto', margin: 0, background: 'var(--surface)',
              border: '1px solid var(--border)', borderRadius: 8, padding: 14,
              fontSize: 12, lineHeight: 1.55, color: 'var(--text)',
              fontFamily: 'var(--font-mono)', whiteSpace: 'pre', minHeight: 180, maxHeight: '46vh',
            }}>
              <code>{result.code}</code>
            </pre>

            <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 14, flexWrap: 'wrap' }}>
              <Button variant="primary" size="sm" onClick={handleCopy}>{copied ? '✓ Copied' : 'Copy code'}</Button>
              <Button variant="secondary" size="sm" onClick={handleDownload}>Download {result.filename}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
